import React, { FC, ReactNode, useContext } from "react";
import { useRouter } from "next/router";
import { ThemeContext } from "styled-components";
import { CaretIconSvg } from "../../icons";
import { Form } from "react-final-form";
import { FieldInput } from "../../fields/FieldInput";
import { AlertTypes } from "../../alerts/Alert";
import { Button } from "../../Button";
import { Heading } from "../../Heading";
import {
  getSortedTermsByLocation,
  TermsAndConditions,
} from "../../terms/TermsAndConditions";
import {
  BrandConfigCustomizationsImage,
  CustomText,
  LocalizationQuery,
} from "../../../types/graphql/generated";
import { parseTemplateAsString } from "../../../helpers/localization";
import { validateWithYup, getLoginSchema } from "../../../helpers/validation";
import { formFields } from "../../../constants";
import {
  StyledAccountLogo,
  StyledAlert,
  StyledError,
  StyledForm,
  StyledHeading,
  StyledLinkWrapper,
  StyledLoginButton,
  StyledLoginForm,
  StyledLogo,
  StyledResetPassword,
  StyledSignup,
} from "./index.styled";

// 12/16/24 PCK-1215: Temporary alert copy. Will be removed after some time.
// Account Update
const accountUpdateTitle = "Aktualizacja konta";
// Our account requirements have changed. You may need to change your password.
const accountUpdateInfo =
  "Nasze wymagania dotyczące konta uległy zmianie. Być może będziesz musiał zmienić hasło.";

interface LoginFormProps {
  accountLogo?: BrandConfigCustomizationsImage;
  alertDetails?: {
    alertType: AlertTypes;
    text: string;
  };
  canSignUp?: boolean;
  customTexts: CustomText[] | undefined;
  error: string | ReactNode;
  goToCreateAccount?: () => void;
  goToForgotPassword: () => void;
  isLoading: boolean;
  isSodaStreamPL?: boolean;
  localization?: LocalizationQuery["localization"] | null;
  logoSrc?: string;
  onSubmit: (formValues: LoginFormValues) => void;
  privacyUrl?: string;
  signinPageTitle?: string | null;
  tenantName: string | undefined;
}

export interface LoginFormValues {
  email: string;
  password: string;
}

export const LoginForm: FC<LoginFormProps> = ({
  accountLogo,
  alertDetails,
  canSignUp,
  customTexts = [],
  error,
  goToCreateAccount,
  goToForgotPassword,
  isLoading,
  isSodaStreamPL,
  localization,
  logoSrc,
  onSubmit,
  signinPageTitle,
  tenantName,
}) => {
  const theme = useContext(ThemeContext);
  const router = useRouter();

  const { accountAltText = "Account", shopLogoAltText = "shop logo" } =
    localization?.portal?.alts || {};
  const {
    emailInputPlaceholderText = "Enter email address",
    passwordInputPlaceholderText = "Enter password",
    loginButtonText = "Login",
    forgotPasswordButtonText = "Forgot your password?",
  } = localization?.forms?.signIn || {};
  const {
    // eslint-disable-next-line no-template-curly-in-string
    signUpHeadingText = "New to ${tenantNameText}?",
    signUpDefaultHeadingText = "New here?",
    signUpButtonText = "Create an account",
  } = localization?.forms?.signIn || {};

  return (
    <StyledLoginForm>
      {!!logoSrc && <StyledLogo src={logoSrc} alt={shopLogoAltText} />}

      {!!accountLogo && (
        <div>
          <StyledLinkWrapper
            onClick={() => {
              router.back();
            }}
          >
            <CaretIconSvg
              pointDirection="left"
              color={theme.color.primary}
              width="14px"
              height="12px"
            />
          </StyledLinkWrapper>
          <StyledAccountLogo src={accountLogo.src} alt={accountAltText} />
        </div>
      )}

      {alertDetails && (
        <StyledAlert bold closable type={alertDetails.alertType}>
          {alertDetails.text}
        </StyledAlert>
      )}

      {isSodaStreamPL && (
        <StyledAlert bold closable title={accountUpdateTitle} type="info">
          <div>{accountUpdateInfo}</div>
        </StyledAlert>
      )}

      <Heading level="h2" size="l">
        {signinPageTitle}
      </Heading>
      <Form
        onSubmit={(formValues: LoginFormValues) => onSubmit(formValues)}
        validate={validateWithYup(
          getLoginSchema(localization?.forms?.validation)
        )}
        render={({ handleSubmit }) => (
          <StyledForm onSubmit={handleSubmit}>
            <FieldInput
              {...formFields.email}
              placeholder={emailInputPlaceholderText}
            />
            <FieldInput
              {...formFields.password}
              type="password"
              placeholder={passwordInputPlaceholderText}
            />
            <TermsAndConditions
              terms={getSortedTermsByLocation({
                location: "login",
                customTexts: customTexts,
              })}
              type="info"
            >
              <StyledLoginButton>
                <Button htmlType="submit" loading={isLoading} fullWidth>
                  {loginButtonText}
                </Button>
              </StyledLoginButton>
              <StyledResetPassword>
                <Button type="text" onClick={goToForgotPassword}>
                  {forgotPasswordButtonText}
                </Button>
              </StyledResetPassword>
            </TermsAndConditions>
            {error && <StyledError>{error}</StyledError>}
          </StyledForm>
        )}
      />
      {canSignUp && (
        <StyledSignup>
          <StyledHeading level="h3" size="m">
            {tenantName
              ? parseTemplateAsString(signUpHeadingText, {
                  tenantNameText: tenantName,
                })
              : signUpDefaultHeadingText}
          </StyledHeading>
          <Button
            type="tertiary"
            fullWidth
            onClick={goToCreateAccount}
            disabled={isLoading}
          >
            {signUpButtonText}
          </Button>
        </StyledSignup>
      )}
    </StyledLoginForm>
  );
};
